import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { addBreadcrumb, captureError, startSpan, logger } from '@/lib/sentry';

/**
 * Hook que monitora chamadas ao Supabase e reporta performance/erros ao Sentry
 * Adiciona breadcrumbs para rastrear sequência de operações antes de erros
 */
export const useSupabasePerformance = () => {
  useEffect(() => {
    // Hooks precisam ser chamados sempre; em dev/desativado, apenas não faz nada.
    if (!import.meta.env.PROD) return;

    // Store the original from function - use Function type since we're proxying dynamically
    // eslint-disable-next-line @typescript-eslint/ban-types
    const originalFrom = supabase.from.bind(supabase) as Function;
    
    // Proxy das chamadas do Supabase para adicionar observabilidade
    // We need to intercept .from() at runtime to add performance monitoring,
    // which requires dynamic typing since we wrap the promise chain.
    (supabase as unknown as Record<string, unknown>).from = (table: string) => {
      const startTime = performance.now();
      
      addBreadcrumb(`Supabase query: ${table}`, { table }, 'info');
      
      const query = originalFrom(table) as Record<string, unknown>;
      
      // Check if query has then method (for promise-like behavior)
      if (typeof query.then !== 'function') {
        return query;
      }
      
      const originalThen = (query.then as Function).bind(query);
      
      // Intercepta resultado para medir tempo com span customizado
      query.then = function(
        onfulfilled?: ((value: unknown) => unknown) | null,
        onrejected?: ((reason: unknown) => unknown) | null
      ) {
        return originalThen(
          (result: { data?: unknown[] | null; error?: { message: string } | null }) => {
            const duration = performance.now() - startTime;
            const count = Array.isArray(result?.data) ? result.data.length : 0;
            
            // Cria span de performance para a query
            startSpan(
              {
                op: 'db.query',
                name: `SELECT FROM ${table}`,
                attributes: {
                  'db.table': table,
                  'db.duration': duration,
                  'db.count': count,
                },
              },
              () => {
                // Log estruturado de sucesso
                logger.debug(`Supabase query success: ${table}`, {
                  table,
                  duration: `${duration.toFixed(2)}ms`,
                  count,
                });
              }
            );
            
            // Reporta queries muito lentas (> 3s)
            if (duration > 3000) {
              logger.warn(`Slow Supabase query detected: ${table}`, {
                table,
                duration,
                threshold: '3000ms',
              });
              
              captureError(
                new Error(`Slow Supabase query: ${table}`),
                {
                  tags: { 
                    query_type: 'slow_query',
                    table,
                  },
                  extra: { 
                    duration,
                    count,
                  },
                }
              );
            }
            
            return onfulfilled ? onfulfilled(result) : result;
          },
          (error: unknown) => {
            const duration = performance.now() - startTime;
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            
            // Adiciona breadcrumb de erro
            addBreadcrumb(
              `Supabase query error: ${table}`,
              { 
                table, 
                duration: `${duration.toFixed(2)}ms`,
                error: errorMessage,
              },
              'error'
            );
            
            // Log estruturado de erro
            logger.error(`Supabase query failed: ${table}`, {
              table,
              duration,
              error: errorMessage,
            });
            
            // Reporta erro ao Sentry com span
            startSpan(
              {
                op: 'db.query.error',
                name: `ERROR: ${table}`,
                attributes: {
                  'db.table': table,
                  'db.duration': duration,
                  'error.message': errorMessage,
                },
              },
              () => {
                captureError(error instanceof Error ? error : new Error(errorMessage), {
                  tags: { 
                    query_type: 'supabase_error',
                    table,
                  },
                  extra: { duration },
                });
              }
            );
            
            return onrejected ? onrejected(error) : Promise.reject(error);
          }
        );
      };
      
      return query;
    };
    
    return () => {
      // Restaura método original no cleanup
      (supabase as unknown as Record<string, unknown>).from = originalFrom;
    };
  }, []);
};
